import React, { useState, useEffect } from 'react';
import { Box, Grid2 as Grid, Skeleton } from '@mui/material';
import { isNull } from 'lodash';

import { PostPhaseStatuses, Steps } from '../../../../../../Utils/Types';
import FormEmbed from '../../../../../DesignSystem/FormEmbed';
import FormShow from './FormShow/FormShow';
import FormIframe from '../../../../../DesignSystem/FormEmbed/FormIframe';
import Question from '../../../../../FormResponses/Results/Question';
import StudyEmptyStates from './EmptyStates/Study';
import { validateCanParticipate } from '../../../../../../Utils/Post/securedStrategies';
import { SteppablePostType } from '../../../../../../Utils/Types/Steps';
import Stats from './Stats';
import { FormQuestion } from '../../../../../../Utils/Types/FormBuilder';
import useSpace from '../../../../../../Hooks/useSpace';
import useCurrentUser from '../../../../../../Hooks/useCurrentUser';

function StudyTabContent({ post, stepTab }: any) {
  const { space } = useSpace();
  const { default_locale: defaultLocale } = space;

  const { isLoggedIn } = useCurrentUser();
  const queryParam = new URLSearchParams(window.location.search);
  const isAnswered = post.has_answered;
  const formBuilderEnabled = queryParam.get('form_builder') === 'true' || post?.form_builder;
  const stepType = post.type as SteppablePostType;
  const [canParticipate, setCanParticipate] = useState<boolean | null>(null);
  const [strategies, setStrategies] = useState([]);
  const hasLoggedInStrategies = (Object.keys(strategies) || []).includes('logged_in');

  useEffect(() => {
    validateCanParticipate(
      post,
      (securedStrategies: any) => {
        setStrategies(securedStrategies);
        setCanParticipate(true);
      },
      (securedStrategies: any) => {
        setStrategies(securedStrategies);
        setCanParticipate(false);
      },
    );
  }, [post]);

  const submissionOngoing =
    stepTab.step_type === Steps[stepType].SUBMISSION &&
    stepTab.dates_status === PostPhaseStatuses.ONGOING;

  if (
    stepTab.step_type === Steps[stepType].IMPLEMENTATION &&
    post.permissions.can_access_study_results &&
    [PostPhaseStatuses.ONGOING, PostPhaseStatuses.AVAILABLE].includes(stepTab.dates_status)
  ) {
    return (
      <>
        {formBuilderEnabled && (
          <Box
            sx={{
              mb: 2,
              px: { xs: 3, sm: 0 },
            }}
          >
            <Stats post={post} />

            <Grid container spacing={3}>
              {post.form_questions
                // NOTE: we get all the form questions from the api
                // but we can only get the answers for the non confidential ones
                // so we filter them to not have a question with no answer
                ?.filter((question: FormQuestion) => !question.confidential)
                ?.map((question: any, index: number) => (
                  <Grid key={question.id} size={12}>
                    <Box sx={{ padding: 2 }}>
                      <Question index={index} question={question} post={post} />
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}

        <FormIframe title={post.title} url={post.form_results_url} />
      </>
    );
  }

  if (isNull(canParticipate)) {
    return (
      <Box
        sx={{
          pb: 3,
          px: { xs: 3, sm: 3 },
        }}
      >
        <Skeleton variant="rectangular" height={65} width="100%" sx={{ bgcolor: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      id="study-content-tab"
      sx={{
        pb: 3,
        px: { xs: 3, sm: 3 },
      }}
    >
      <StudyEmptyStates
        post={post}
        stepTab={stepTab}
        isLoggedIn={isLoggedIn}
        canParticipate={canParticipate}
        isAnswered={isAnswered}
        hasLoggedInStrategies={hasLoggedInStrategies}
      />

      {canParticipate && post.form_builder && (submissionOngoing || isAnswered) && (
        <Box
          sx={{
            mt: 3,
          }}
        >
          <FormShow
            lang={defaultLocale}
            post={post}
            submissionOngoing={submissionOngoing}
            isAnswered={isAnswered}
          />
        </Box>
      )}

      {canParticipate && !post.form_builder && submissionOngoing && !isAnswered && (
        <Box
          sx={{
            mt: 3,
          }}
        >
          <FormEmbed post={post} />
        </Box>
      )}
    </Box>
  );
}

export default StudyTabContent;
